import * as React from 'react';

import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { useLocation } from 'wouter';

import {
  $activateAutomation,
  $pauseAutomation,
  $deleteAutomation,
  $duplicateAutomation,
  $testAutomation,
} from '../../../_state/automationDetail.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { automationsRoutes } from '../../../routes';

export type UseAutomationActionsOptions = {
  automationId: string | undefined;
  onDeleteSuccess?: () => void;
};

export const useAutomationActions = ({
  automationId,
  onDeleteSuccess,
}: UseAutomationActionsOptions) => {
  const t = useTranslations();
  const [, navigate] = useLocation();
  const toast = useToast();

  const activateAutomation = useSetAtom($activateAutomation);
  const pauseAutomation = useSetAtom($pauseAutomation);
  const deleteAutomation = useSetAtom($deleteAutomation);
  const duplicateAutomation = useSetAtom($duplicateAutomation);
  const testAutomation = useSetAtom($testAutomation);

  const handleActivate = React.useCallback(async () => {
    if (!automationId) return;
    try {
      await activateAutomation({ id: automationId });
      toast.success(t.detail.messages.activated);
    } catch (err) {
      toast.error(
        t.detail.messages.activateFailed(
          err instanceof Error ? err.message : '',
        ),
      );
    }
  }, [automationId, activateAutomation, toast, t]);

  const handlePause = React.useCallback(async () => {
    if (!automationId) return;
    try {
      await pauseAutomation({ id: automationId });
      toast.success(t.detail.messages.paused);
    } catch (err) {
      toast.error(
        t.detail.messages.pauseFailed(err instanceof Error ? err.message : ''),
      );
    }
  }, [automationId, pauseAutomation, toast, t]);

  const handleDelete = React.useCallback(async () => {
    if (!automationId) return;

    try {
      await deleteAutomation({ id: automationId });
      toast.success(t.detail.messages.deleted);
      onDeleteSuccess?.();
    } catch (err) {
      toast.error(
        t.detail.messages.deleteFailed(err instanceof Error ? err.message : ''),
      );
    }
  }, [automationId, deleteAutomation, toast, onDeleteSuccess, t]);

  const handleDuplicate = React.useCallback(async () => {
    if (!automationId) return;
    try {
      const result = await duplicateAutomation({ id: automationId });
      toast.success(t.detail.messages.duplicated);
      const newId = result?.automationsAutomationDuplicate?.id;
      if (newId) {
        navigate(automationsRoutes.detail.replace(':id', newId));
      }
    } catch (err) {
      toast.error(
        t.detail.messages.duplicateFailed(
          err instanceof Error ? err.message : '',
        ),
      );
    }
  }, [automationId, duplicateAutomation, toast, navigate, t]);

  const handleTest = React.useCallback(
    async (testPayload?: Record<string, unknown>) => {
      if (!automationId) return;
      try {
        await testAutomation({ id: automationId, testPayload });
        toast.success(t.detail.messages.tested);
      } catch (err) {
        toast.error(
          t.detail.messages.testFailed(err instanceof Error ? err.message : ''),
        );
      }
    },
    [automationId, testAutomation, toast, t],
  );

  return {
    handleActivate,
    handlePause,
    handleDelete,
    handleDuplicate,
    handleTest,
  };
};
